import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import {
  GroupTypeDetails,
  CollectorGroupSettingsInput,
  CollectorSettingQuery
} from 'graphql/types';
import { EnabledDisabled } from 'modules/Settings/constant';
import {
  UpdateSettingPreferencesForCollectorSettingInput,
  useUpdateSettingPreferencesForCollectorSettingMutation
} from 'graphql/types';
import { CollectorManagePageSkeleton } from '../../CollectorManagePageSkeleton';
import {
  useCompanyCollectorSettingsForm,
  FormData,
  mapToAccessSope
} from './hooks';
import { CollectorSettingsBody } from './CollectorSettingsBody';

export type CompanyCollectorSettingsPageProps = {
  enabledGroupTypes: GroupTypeDetails[];
  installId: string;
  data: CollectorSettingQuery | undefined;
  loading: boolean;
  retryUrl: string;
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    overflowY: 'hidden'
  },
  container: {
    display: 'flex',
    margin: theme.spacing(-2.5, 0)
  },
  verticalDivider: {
    backgroundColor: theme.palette.grey[100]
  },
  dialogContent: {
    paddingLeft: 0
  }
}));

export const CompanyCollectorSettingsPage: React.FC<CompanyCollectorSettingsPageProps> = ({
  enabledGroupTypes,
  installId,
  data,
  loading,
  retryUrl
}: CompanyCollectorSettingsPageProps) => {
  const collectorInstallErrors =
    data?.collectorSetting?.item?.installStatus?.errors;

  const {
    formMethods,
    defaultEnabled,
    defaultOptedIn
  } = useCompanyCollectorSettingsForm(data);

  const [
    updateSettingPreferencesMutation,
    { loading: saving }
  ] = useUpdateSettingPreferencesForCollectorSettingMutation();
  const [defaultValues, setDefaultValues] = useState<any>();
  const { control, reset, formState } = formMethods;
  const { isDirty, isSubmitSuccessful } = formState;

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ ...defaultValues });
    }
  }, [reset, defaultValues, isSubmitSuccessful]);

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'rows'
  });
  const classes = useStyles({ rows: fields.length });

  const onSubmit = useCallback(
    async (formData: FormData) => {
      const {
        rows,
        defaultRow: { enableDisabled, optedIn }
      } = formData;
      const groupSettings: CollectorGroupSettingsInput[] = rows.map(
        ({ enableDisabled, optedIn, ...r }) => ({
          enabled: enableDisabled === EnabledDisabled.enabled,
          canOptOut: optedIn === EnabledDisabled.enabled,
          accessScopeIn: {
            ...mapToAccessSope('departments', r.DEPARTMENT),
            ...mapToAccessSope('employeeTypes', r.EMPLOYEE_TYPE),
            ...mapToAccessSope('locations', r.LOCATION),
            ...mapToAccessSope('costCenters', r.COST_CENTER),
            ...mapToAccessSope('divisions', r.DIVISION),
            ...mapToAccessSope('serviceCenters', r.SERVICE_CENTER)
          }
        })
      );

      const input: UpdateSettingPreferencesForCollectorSettingInput = {
        installId: installId,
        settings: [
          {
            key: 'collectorEnabled',
            value: String(enableDisabled === EnabledDisabled.enabled)
          },
          {
            key: 'allowUsersToOptOutEnabled',
            value: String(optedIn === EnabledDisabled.enabled)
          }
        ],
        groupSettings: enabledGroupTypes.length ? groupSettings : []
      };

      await updateSettingPreferencesMutation({
        variables: {
          input: input
        }
      });
      setDefaultValues(formData);
    },
    [enabledGroupTypes.length, installId, updateSettingPreferencesMutation]
  );

  return (
    //to fix type instantiation is excessively deep and infinite
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <FormProvider {...formMethods}>
      <div className={classes.dialogContent}>
        {loading ? (
          <div data-testid="loading-skeleton-screen-tenant">
            <CollectorManagePageSkeleton />
          </div>
        ) : (
          <div className={classes.container}>
            <CollectorSettingsBody
              defaultEnabled={defaultEnabled}
              defaultOptedIn={defaultOptedIn}
              enabledGroupTypes={enabledGroupTypes}
              fields={fields}
              errors={collectorInstallErrors}
              fieldName={'rows'}
              prepend={prepend}
              remove={remove}
              onSave={onSubmit}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              handleSubmit={formMethods.handleSubmit}
              loading={loading || saving}
              installId={data?.collectorSetting?.item?.installId}
              retryUrl={retryUrl}
              isDirty={isDirty}
            />
          </div>
        )}
      </div>
    </FormProvider>
  );
};
